import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { formatDate } from "@/lib/format";

/**
 * Three facts a seller has to see before dialling, printed beside the status
 * wherever a company is named: the prospect list, its file, reception, and the
 * event modal the agents work their day from.
 *
 * *No contactar* — somebody there asked never to be contacted again, through
 * *Ya no llamar ni contactar este prospecto* (`Company.do_not_contact_at`).
 *
 * *Grabación avisada* — somebody there has been told the calls are recorded:
 * the *Grabación: Sí* of the call wrap-up (`recording_notice_given`).
 *
 * *Grabación rechazada* — somebody there was told, and said no: the
 * *Grabación: No* (`recording_refused`). Not the opposite of the one above —
 * one person may have said yes and another no, and then both show.
 *
 * Optional fields, because reception names a company in shapes that carry
 * some and not others.
 */
export function CompanyTags({
  company,
}: {
  company: {
    do_not_contact_at?: string | null;
    recording_notice_given?: boolean;
    recording_refused?: boolean;
  };
}) {
  const { t } = useTranslation();
  return (
    <>
      {company.do_not_contact_at ? (
        <Badge
          tone="stop"
          title={t("companyTags.doNotContactHint", {
            date: formatDate(company.do_not_contact_at),
          })}
        >
          {t("companyTags.doNotContact")}
        </Badge>
      ) : null}
      {company.recording_notice_given ? (
        <Badge tone="told" title={t("companyTags.recordingNoticeHint")}>
          {t("companyTags.recordingNotice")}
        </Badge>
      ) : null}
      {company.recording_refused ? (
        <Badge tone="refused" title={t("companyTags.recordingRefusedHint")}>
          {t("companyTags.recordingRefused")}
        </Badge>
      ) : null}
    </>
  );
}

/**
 * One person's answer to *Grabación*, for the places that list people: the
 * contact cards of a company's file and the *Contactar* block of the event
 * modal. Nothing for "never asked" — that is not a fact worth a badge.
 */
export function ContactRecordingTag({ answer }: { answer?: boolean | null }) {
  const { t } = useTranslation();
  if (answer === true) {
    return (
      <Badge tone="told" title={t("companyTags.contactRecordingNoticeHint")}>
        {t("companyTags.recordingNotice")}
      </Badge>
    );
  }
  if (answer === false) {
    return (
      <Badge tone="refused" title={t("companyTags.contactRecordingRefusedHint")}>
        {t("companyTags.recordingRefused")}
      </Badge>
    );
  }
  return null;
}

type BadgeTone = "stop" | "told" | "refused";

/**
 * Colour, which the rest of the system does without, on purpose: the palette
 * is mono and the red accent is spoken for, so these three would read like any
 * other status. Each gets its own hue, a border and a glyph — the glyph so the
 * difference survives a colour-blind reader and a black-and-white print.
 *
 *   stop    → solid red, the one that says "don't"
 *   told    → green: already said, no need to say it again
 *   refused → amber: said, and declined — mind what is recorded
 */
const BADGE_TONES: Record<BadgeTone, { className: string; glyph: string }> = {
  stop: { className: "border-brand-800 bg-brand-600 text-ground", glyph: "⊘" },
  told: {
    className: "border-emerald-700 bg-emerald-100 text-emerald-900",
    glyph: "✓",
  },
  refused: {
    className: "border-amber-600 bg-amber-100 text-amber-900",
    glyph: "✕",
  },
};

function Badge({
  tone,
  title,
  children,
}: {
  tone: BadgeTone;
  title: string;
  children: ReactNode;
}) {
  const { className, glyph } = BADGE_TONES[tone];
  return (
    <span
      title={title}
      className={`inline-flex shrink-0 items-center gap-1.5 border-2 px-2 py-0.5 text-[11px] leading-tight font-bold tracking-[0.06em] whitespace-nowrap uppercase ${className}`}
    >
      <span aria-hidden="true">{glyph}</span>
      {children}
    </span>
  );
}
